import forge from 'apis/forge';
import { DesignSystem } from 'dragonmouse-shared/interfaces';
import { MenuItem, MenuItemAction, MenuItemDivider } from 'menu/MenuItem';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { showContextMenu } from 'redux/mode/actions';
import { displayConfirmModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { selectIsMobile } from 'redux/mode/reducers';
import { useMobileTapBehavior } from 'hooks/useMobileTapBehavior';
import { useNavigate } from 'react-router';
import * as routes from 'routes';
import { trackDesignSystemAction } from 'analytics';
import { useState } from 'react';
import ShareModal from 'components/Modal/ShareModal';
import * as S from '../styles';

interface Props {
	designSystem: DesignSystem;
	isActive?: boolean;
	onClick?: () => void;
	onCommandClick?: () => void;
}

const DesignSystemListItem = ({ designSystem, isActive, onClick, onCommandClick }: Props) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [showShareModal, setShowShareModal] = useState(false);
	const { isMobile, activeDesignSystemId } = useSelector(
		(r: RootReducer) => ({
			isMobile: selectIsMobile(r.mode),
			activeDesignSystemId: r.mode.activeDesignSystemId,
		}),
		shallowEqual
	);

	const { isPressed, touchHandlers } = useMobileTapBehavior(isMobile);

	const menuItems: MenuItem[] = [
		new MenuItemAction('Open design system', '', () => {
			onClick && onClick();
		}),
		new MenuItemAction('Open design system in new tab', '', () => {
			window.open(
				routes.designSystem(designSystem.id, designSystem.title),
				'_blank',
				'noopener,noreferrer'
			);
		}),
		new MenuItemDivider(),
		MenuItemAction.createRenameDesignSystem(designSystem),
		new MenuItemDivider(),
		new MenuItemAction('Share...', '', () => {
			trackDesignSystemAction('open_share_modal');
			setShowShareModal(true);
		}),
		new MenuItemAction('Delete design system...', '', async () => {
			const { emails, pendingEmails } = await forge.getDesignSystemUsers({
				designSystemId: designSystem.id,
			});
			const otherCount = emails.length + pendingEmails.length - 1;
			const description =
				otherCount > 0
					? `${otherCount} other user${
							otherCount === 1 ? ' has' : 's have'
					  } access. Deleting will remove their access permanently. This cannot be undone.`
					: 'This will permanently delete the design system and all its versions. This cannot be undone.';
			displayConfirmModal({
				title: `Delete ${designSystem.title}?`,
				description,
				submitLabel: 'Delete',
				onCancel: () => {},
				onSubmit: async () => {
					await forge.deleteDesignSystem(
						{
							designSystemId: designSystem.id,
						},
						true
					);
					if (designSystem.id === activeDesignSystemId) navigate(routes.home(), { replace: true });
				},
			});
		}),
	];

	const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		if (e.metaKey || e.ctrlKey) {
			onCommandClick && onCommandClick();
		} else if (onClick) {
			onClick();
		}
	};

	const onContextMenu = (e: React.MouseEvent) => {
		if (isMobile) return;
		dispatch(
			showContextMenu({
				point: { x: e.clientX + 5, y: e.clientY },
				menuItems: menuItems,
			})
		);
	};

	const handleContainerTap = () => {
		if (onClick) {
			onClick();
		}
	};

	return (
		<>
			<S.Base
				isActive={isActive}
				onClick={isMobile ? handleContainerTap : handleClick}
				onContextMenu={isMobile ? undefined : onContextMenu}
				title={designSystem.title}
				data-pressed={isPressed}
				{...touchHandlers}
			>
				<S.Title isActive={isActive}>{designSystem.title}</S.Title>
			</S.Base>
			{showShareModal && (
				<ShareModal
					kind="designSystem"
					entityId={designSystem.id}
					onClose={() => setShowShareModal(false)}
				/>
			)}
		</>
	);
};

export default DesignSystemListItem;
